import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import msiData from '../data/msi.json';
import evenementsData from '../data/evenements.json';
import etoilesData from '../data/etoiles.json';
import Header from '../widgets/molecules/header';

function hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

function SlotCard({ slot }) {
    const region = msiData.regions[slot.region];
    const [regionImgError, setRegionImgError] = useState(false);
    const [teamImgError, setTeamImgError] = useState(false);

    return (
        <div className="flex flex-col items-center gap-1">
            {/* Label au-dessus */}
            <div className="flex items-baseline gap-1.5 self-start pl-1">
                <span
                    className="text-sm font-bold tracking-widest uppercase"
                    style={{ color: region.couleur }}
                >
                    Seed #{slot.seed}
                </span>
                <span className="text-sm text-gray-400 font-semibold">· {region.nom}</span>
            </div>

            {/* Carte visuelle */}
            <div
                className="relative w-full rounded-xl overflow-hidden bg-[#111111] aspect-square"
                style={{
                    border: `2px solid ${region.couleur}`,
                    boxShadow: slot.equipe ? `0 0 16px 2px ${hexToRgba(region.couleur, 0.25)}` : 'none',
                }}
            >
                {/* Background région */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" style={{ display: slot.equipe ? 'none' : 'flex' }}>
                    {region.logo && !regionImgError ? (
                        <img
                            src={`${process.env.PUBLIC_URL}/images/regions/${region.logo}`}
                            alt=""
                            className="w-4/5 h-4/5 object-contain"
                            onError={() => setRegionImgError(true)}
                        />
                    ) : (
                        <span className="font-black text-5xl leading-none" style={{ color: region.couleur }}>
                            {region.nom}
                        </span>
                    )}
                </div>

                {/* Logo équipe centré */}
                {slot.equipe && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <a
                            href={slot.equipe.leaguepedia || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-full h-full hover:scale-110 transition-transform duration-150"
                            onClick={e => !slot.equipe.leaguepedia && e.preventDefault()}
                        >
                            {!teamImgError ? (
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/equipes/${slot.equipe.logo}`}
                                    alt={slot.equipe.name}
                                    className="w-3/5 h-3/5 object-contain drop-shadow-lg"
                                    onError={() => setTeamImgError(true)}
                                />
                            ) : (
                                <div
                                    className="w-12 h-12 flex items-center justify-center rounded-full text-sm font-bold"
                                    style={{ background: hexToRgba(region.couleur, 0.2), color: region.couleur }}
                                >
                                    {slot.equipe.teamtag}
                                </div>
                            )}
                        </a>
                    </div>
                )}
            </div>

            {/* Nom équipe en dessous */}
            {slot.equipe && (
                <span className="text-sm font-bold text-white text-center leading-tight mt-1">
                    {slot.equipe.name}
                </span>
            )}
        </div>
    );
}

function MSIPage() {
    const navigate = useNavigate();
    const liens = etoilesData.etoiles.map(e => e.lien);
    const evenementData = evenementsData.evenements.find(e => e.titre === 'MSI');

    const totalSlots = msiData.slots.length;
    const confirmedSlots = msiData.slots.filter(s => s.equipe !== null).length;

    return (
        <>
            <div className="bg-image"></div>
            <div className="flex flex-col min-h-screen md:h-screen text-white relative">
                <Header etoiles={liens} />

                <div className="bg-[#1A1A1A] bg-opacity-70 backdrop-blur-sm border-b border-gray-700 px-4 md:px-8 py-3 md:py-4">
                    <div className="flex items-center gap-3 md:gap-6">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
                        >
                            <span className="text-3xl md:text-4xl">←</span>
                        </button>

                        <div className="flex items-center gap-3 md:gap-6 flex-1 flex-wrap">
                            <img
                                src={`${process.env.PUBLIC_URL}/images/evenements/msi.png`}
                                alt="MSI"
                                className="w-14 h-14 md:w-20 md:h-20 rounded-lg shadow-lg flex-shrink-0"
                            />
                            <div className="text-left flex-1">
                                <h1 className="text-lg md:text-2xl font-bold mb-1 md:mb-2 font-poppins">MSI 2026</h1>
                                <div className="flex flex-wrap gap-3 md:gap-6 text-sm items-center">
                                    <div className="flex gap-2 text-gray-300">
                                        <span>{evenementData?.payslieu}</span>
                                        <span>•</span>
                                        <span>{evenementData?.lieu}</span>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 text-xs mb-0.5">Début</p>
                                        <p className="font-semibold">
                                            {evenementData && new Date(evenementData.date).toLocaleDateString('fr-FR', { weekday: 'short', month: 'short', day: 'numeric' })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <p className="text-2xl font-bold font-poppins">
                                    {confirmedSlots}
                                    <span className="text-gray-500 text-lg">/{totalSlots}</span>
                                </p>
                                <p className="text-xs text-gray-400">qualifiés</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 md:px-8 pt-6 pb-8">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 max-w-5xl mx-auto">
                        {msiData.slots.map((slot, i) => (
                            <SlotCard key={i} slot={slot} />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}

export default MSIPage;
